import React from 'react';
import { Handle, Position, HandleProps, useNodes, useEdges, useReactFlow } from 'reactflow';
import { Box, Typography, Chip, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch, Stack, Tooltip } from '@mui/material';
import {
  Psychology as LLMIcon,
  MenuBook as KnowledgeIcon,
  Build as ToolsIcon,
  TextSnippet as PromptsIcon,
  SmartToy as AgentIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Code as ParserIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import './AgentNode.css';
import { KnowledgeBase } from '../../../../Shared-ui/Types/KnowledgeBase';
import Tool from '../../../../Shared-ui/Types/Tool';
import Template from '../../../../Shared-ui/Types/Template';
import BaseInstance from '../../../../Services/AxiosInstance/BaseInstance';
import { KNOWLEDGE_BASE, MODELS } from '../../../../Services/Endpoint';

interface CustomNodeProps {
  data: {
    label: string;
    category: 'LLM Models' | 'Knowledge Bases' | 'Tools' | 'Prompts' | 'Agent' | 'Parser';
    description?: string;
    icon?: React.ReactNode;
    agentName?: string;
    knowledgeBase?: KnowledgeBase;
    tool?: Tool;
    template?: Template;
    provider?: string;
    model?: string;
    models?: string[];
    modelsAndProvider?: Record<string, string[]>;
    agentData?: any; // Add agent data for settings
    apiKey?: string; // Add API key for LLM nodes
    parserAttributes?: Array<{ name: string; description: string; dataType: string }>; // Add parser attributes with data type
  };
  id: string;
  onDelete?: (nodeId: string) => void;
}

interface HandleWithLabelProps extends HandleProps {
  label: string;
  labelColor?: string;
  style?: React.CSSProperties;
}

const HandleWithLabel: React.FC<HandleWithLabelProps> = ({ label, labelColor = '#333', style, ...handleProps }) => {
  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <Handle
        {...handleProps}
        style={{
          width: 12,
          height: 12,
          border: '2px solid white',
          borderRadius: '50%',
          position: 'relative',
          zIndex: 10,
          ...style,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.7rem',
          color: labelColor,
          fontWeight: 'medium',
          whiteSpace: 'nowrap',
          marginLeft: handleProps?.position === Position.Left ? 0 : 8,
          marginRight: handleProps?.position === Position.Right ? 0 : 8,
          zIndex: 5,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const DeleteButton: React.FC<{ nodeId: string; onDelete?: (nodeId: string) => void; isAgentNode?: boolean }> = ({ nodeId, onDelete, isAgentNode = false }) => {
  const { deleteElements } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteElements({ nodes: [{ id: nodeId }] });
  };

  // Different positioning for AgentNode to avoid gear icon conflict
  const buttonStyle = isAgentNode ? {
    position: 'absolute' as const,
    top: 2,
    left: 2, // Position on the left side for AgentNode
    width: 16,
    height: 16,
  } : {
    position: 'absolute' as const,
    top: 2,
    right: 2, // Position on the right side for other nodes
    width: 16,
    height: 16,
  };

  return (
    <Tooltip title="Delete Node" placement="top">
      <IconButton
        size="small"
        onClick={handleDelete}
        sx={{
          ...buttonStyle,
          padding: '2px',
          minWidth: '16px',
          backgroundColor: 'transparent',
          color: 'white',
          border: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
          },
          zIndex: 1000,
          fontSize: '12px',
          boxShadow: 'none',
        }}
      >
        <DeleteIcon sx={{ fontSize: '12px' }} />
      </IconButton>
    </Tooltip>
  );
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'LLM Models':
      return <LLMIcon sx={{ fontSize: 20 }} />;
    case 'Knowledge Bases':
      return <KnowledgeIcon sx={{ fontSize: 20 }} />;
    case 'Tools':
      return <ToolsIcon sx={{ fontSize: 20 }} />;
    case 'Prompts':
      return <PromptsIcon sx={{ fontSize: 20 }} />;
    case 'Agent':
      return <AgentIcon sx={{ fontSize: 20 }} />;
    case 'Parser':
      return <ParserIcon sx={{ fontSize: 20 }} />;
    default:
      return null;
  }
};

const getCategoryColor = (category: string) => {
  return '#666666'; // All categories use the same grey color
};

export const CustomNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const categoryColor = getCategoryColor(data.category);

  return (
    <Box
      sx={{
        minWidth: 200,
        backgroundColor: 'white',
        border: `1px solid ${categoryColor}`,
        borderRadius: 2,
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        padding: 2,
        position: 'relative',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: categoryColor, width: 8, height: 8 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {getCategoryIcon(data.category)}
        <Typography variant="subtitle2" fontWeight="bold" color={categoryColor}>
          {data.label}
        </Typography>
      </Box>

      <Chip
        label={data.category}
        size="small"
        sx={{
          backgroundColor: categoryColor,
          color: 'white',
          fontSize: '0.7rem',
          height: 20,
          mb: 1,
        }}
      />

      {data.description && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {data.description}
        </Typography>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: categoryColor, width: 8, height: 8 }}
      />
    </Box>
  );
});

// Specific node types for each category
export const LLMNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [selectedModel, setSelectedModel] = React.useState(data.model || '');
  const [apiKey, setApiKey] = React.useState(data.apiKey || '');
  const [availableModels, setAvailableModels] = React.useState<string[]>(data.models || []);
  const [loading, setLoading] = React.useState(false);

  // Fetch models for this provider on component mount
  React.useEffect(() => {
    const fetchModelsForProvider = async () => {
      if (!data.provider) return;

      try {
        setLoading(true);
        const response = await BaseInstance.get(MODELS);
        const allModels = response.data;

        if (allModels[data.provider]) {
          setAvailableModels(allModels[data.provider]);
          // Update data.models with fresh data
          data.models = allModels[data.provider];
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        // Keep using existing models if fetch fails
        if (data.models && data.models.length > 0) {
          setAvailableModels(data.models);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchModelsForProvider();
  }, [data.provider]);

  // Update state when data changes (for restored nodes)
  React.useEffect(() => {
    if (data.model && data.model !== selectedModel) {
      setSelectedModel(data.model);
    }
    if (data.apiKey && data.apiKey !== apiKey) {
      setApiKey(data.apiKey);
    }
  }, [data.model, data.apiKey]);

  const handleModelChange = (event: any) => {
    const model = event.target.value;
    setSelectedModel(model);
    // Update the node data
    data.model = model;
    data.label = `${data.provider}:${model}`;
  };

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.value;
    setApiKey(key);
    // Update the node data
    data.apiKey = key;
  };

  return (
    <div
      style={{
        width: 200,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          {data.provider === 'OpenAI' ? 'Models' : (data.provider || 'LLM')}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Provider Info */}
        <div style={{ marginBottom: 6 }}>
          <Typography variant="caption" color="text.secondary" style={{ fontSize: '8px' }}>
            {data.description || `${data.provider} models`}
          </Typography>
        </div>


        {/* Model Selection */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: '8px', display: 'block', marginBottom: '2px', color: '#666' }}>
            Model
          </label>
          <select
            value={selectedModel}
            onChange={(e) => handleModelChange({ target: { value: e.target.value } })}
            disabled={loading}
            style={{
              width: '100%',
              fontSize: '9px',
              height: '24px',
              padding: '2px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: loading ? '#f0f0f0' : 'white'
            }}
          >
            <option value="">{loading ? 'Loading models...' : 'Select Model'}</option>
            {availableModels && availableModels.length > 0 ? (
              availableModels.map((model: string) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))
            ) : (
              <option disabled>No models available</option>
            )}
          </select>
        </div>

        {/* API Key */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: '8px', display: 'block', marginBottom: '2px', color: '#666' }}>
            API Key
          </label>
          <input
            type="password"
            placeholder="Enter API key"
            value={apiKey}
            onChange={handleApiKeyChange}
            style={{
              width: '100%',
              padding: '3px 5px',
              border: '1px solid #ddd',
              borderRadius: 2,
              backgroundColor: '#f5f5f5',
              fontSize: '9px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Response
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#1976d2',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export const KnowledgeBaseNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [knowledgeBases, setKnowledgeBases] = React.useState<KnowledgeBase[]>([]);
  const [selectedKB, setSelectedKB] = React.useState<KnowledgeBase | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showDocuments, setShowDocuments] = React.useState(false);

  // Initialize with existing knowledge base data if available
  React.useEffect(() => {
    if (data.knowledgeBase && !selectedKB) {
      setSelectedKB(data.knowledgeBase);
    }
  }, [data.knowledgeBase, selectedKB]);

  // Fetch knowledge bases on component mount
  React.useEffect(() => {
    const fetchKnowledgeBases = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(`${KNOWLEDGE_BASE}?page=1&page_size=100`);
        const kbs = response.data.items || [];

        // Create lightweight versions without documents for performance
        const lightweightKBs = kbs.map((kb: KnowledgeBase) => ({
          ...kb,
          documents: [] // Remove documents to prevent performance issues
        }));

        setKnowledgeBases(lightweightKBs);

        // If we have existing knowledge base data, use it
        if (data.knowledgeBase && !selectedKB) {
          // Find the matching KB from the fetched list
          const matchingKB = lightweightKBs.find((kb: KnowledgeBase) => kb.id === data.knowledgeBase?.id);
          if (matchingKB) {
            setSelectedKB(matchingKB);
            data.knowledgeBase = matchingKB;
            data.label = matchingKB.name;
          } else {
            // If the saved KB is not found, keep the existing data but don't auto-select
            setSelectedKB(data.knowledgeBase);
          }
        } else if (!selectedKB && lightweightKBs.length > 0) {
          // Only auto-select first KB if no existing data and this is a new node
          const firstKB = lightweightKBs[0];
          setSelectedKB(firstKB);
          data.knowledgeBase = firstKB;
          data.label = firstKB.name;
        }
      } catch (error) {
        console.error('Failed to fetch knowledge bases:', error);
        setKnowledgeBases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeBases();
  }, []); // Remove data.knowledgeBase from dependencies to prevent infinite loop

  const handleKBChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const kbId = event.target.value;
    const selectedKnowledgeBase = knowledgeBases.find(kb => kb.id === kbId);

    if (selectedKnowledgeBase) {
      setSelectedKB(selectedKnowledgeBase);
      data.knowledgeBase = selectedKnowledgeBase;
      data.label = selectedKnowledgeBase.name;
      setShowDocuments(false); // Hide documents when changing KB
    }
  };

  const toggleDocuments = () => {
    setShowDocuments(!showDocuments);
  };

  return (
    <div
      style={{
        width: 200,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          Knowledge Base
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Knowledge Base Selection */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: '8px', display: 'block', marginBottom: '2px', color: '#666' }}>
            Select Knowledge Base
          </label>
          <select
            value={selectedKB?.id || ''}
            onChange={handleKBChange}
            disabled={loading}
            style={{
              width: '100%',
              fontSize: '9px',
              height: '24px',
              padding: '2px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          >
            <option value="">{loading ? 'Loading...' : 'Select Knowledge Base'}</option>
            {knowledgeBases.map((kb) => (
              <option key={kb.id} value={kb.id}>
                {kb.name}
              </option>
            ))}
          </select>
        </div>

        {/* Knowledge Base Info */}
        {selectedKB && (
          <div style={{ marginBottom: 6 }}>
            <Typography variant="caption" color="text.secondary" style={{ fontSize: '8px' }}>
              {selectedKB.description || `${selectedKB.type} knowledge base`}
            </Typography>

            {/* Document Count and Toggle */}
            {selectedKB.documents && selectedKB.documents.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <button
                  onClick={toggleDocuments}
                  style={{
                    fontSize: '7px',
                    padding: '2px 4px',
                    border: '1px solid #ccc',
                    borderRadius: '2px',
                    backgroundColor: showDocuments ? '#e3f2fd' : 'white',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  {showDocuments ? 'Hide' : 'Show'} Documents ({selectedKB.documents.length})
                </button>
              </div>
            )}
          </div>
        )}

        {/* Documents Section - Only show when toggled */}
        {showDocuments && selectedKB && selectedKB.documents && selectedKB.documents.length > 0 && (
          <div style={{
            marginBottom: 6,
            maxHeight: '100px',
            overflowY: 'auto',
            border: '1px solid #eee',
            borderRadius: '4px',
            padding: '4px'
          }}>
            <div style={{ fontSize: '7px', fontWeight: 'bold', marginBottom: '2px', color: '#666' }}>
              Documents:
            </div>
            {selectedKB.documents.slice(0, 5).map((doc, index) => (
              <div key={index} style={{
                fontSize: '6px',
                color: '#888',
                marginBottom: '1px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {doc.title || `Document ${index + 1}`}
              </div>
            ))}
            {selectedKB.documents.length > 5 && (
              <div style={{ fontSize: '6px', color: '#999', fontStyle: 'italic' }}>
                ... and {selectedKB.documents.length - 5} more
              </div>
            )}
          </div>
        )}

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Knowledge
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#388e3c',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export const ToolNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  return (
    <div
      style={{
        width: 160,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          {data.tool?.name || 'Tool'}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Tool Info */}
        <div style={{ marginBottom: 6 }}>
          <Typography variant="caption" color="text.secondary" style={{ fontSize: '8px' }}>
            {data.tool?.description || 'Tool node'}
          </Typography>
        </div>

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Result
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#f57c00',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export const PromptNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [templates, setTemplates] = React.useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<Template | null>(data.template || null);
  const [loading, setLoading] = React.useState(false);

  // Import Templates from the constant
  React.useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        // Import Templates dynamically to avoid circular dependency
        const { Templates } = await import('../../../../Constant/Templates');
        setTemplates(Templates);

        // If no template is selected but we have templates available, select the first one
        if (!selectedTemplate && Templates.length > 0) {
          setSelectedTemplate(Templates[0]);
          data.template = Templates[0];
          data.label = Templates[0].name;
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const handleTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = event.target.value;
    const selectedTemplateData = templates.find(template => template.id === templateId);

    if (selectedTemplateData) {
      setSelectedTemplate(selectedTemplateData);
      data.template = selectedTemplateData;
      data.label = selectedTemplateData.name;
    }
  };

  return (
    <div
      style={{
        width: 200,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          Prompt
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Template Selection */}
        <div style={{ marginBottom: 6 }}>
          <label style={{ fontSize: '8px', display: 'block', marginBottom: '2px', color: '#666' }}>
            Select Template
          </label>
          <select
            value={selectedTemplate?.id || ''}
            onChange={handleTemplateChange}
            disabled={loading}
            style={{
              width: '100%',
              fontSize: '9px',
              height: '24px',
              padding: '2px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          >
            <option value="">{loading ? 'Loading...' : 'Select Template'}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        {/* Template Info */}
        {selectedTemplate && (
          <div style={{ marginBottom: 6 }}>
            <Typography variant="caption" color="text.secondary" style={{ fontSize: '8px' }}>
              {selectedTemplate.description || 'Prompt template'}
            </Typography>
          </div>
        )}

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Prompt
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#7b1fa2',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export const CustomPromptNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  // Initialize with empty string to avoid processing large text immediately
  const [promptText, setPromptText] = React.useState('');
  const [templates, setTemplates] = React.useState<Template[]>([]);
  const [showTemplateDialog, setShowTemplateDialog] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isContentLoaded, setIsContentLoaded] = React.useState(false);
  const hasLoadedContent = React.useRef(false);

  // Load the actual content after component mounts to avoid blocking render
  React.useEffect(() => {
    if (data.template?.content && !hasLoadedContent.current) {
      // For large content, load it asynchronously
      if (data.template.content.length > 1000) {
        setTimeout(() => {
          setPromptText(data.template!.content);
          setIsContentLoaded(true);
          hasLoadedContent.current = true;
        }, 100);
      } else {
        setPromptText(data.template.content);
        setIsContentLoaded(true);
        hasLoadedContent.current = true;
      }
    }
  }, [data.template?.content]);

  // Truncate text for display to improve performance
  const displayText = React.useMemo(() => {
    if (!isContentLoaded) return 'Loading content...';
    return promptText.length > 200
      ? promptText.substring(0, 200) + '...'
      : promptText;
  }, [promptText, isContentLoaded]);

  // Import Templates from the constant
  React.useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        // Import Templates dynamically to avoid circular dependency
        const { Templates } = await import('../../../../Constant/Templates');
        setTemplates(Templates);
      } catch (error) {
        console.error('Failed to load templates:', error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Only allow editing if text is short, otherwise redirect to popup
    if (promptText.length > 200) {
      setShowPopup(true);
      return;
    }

    setPromptText(event.target.value);
    // Update the template content in the node data
    if (data.template) {
      data.template.content = event.target.value;
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setPromptText(template.content);
    // Update the template content in the node data
    if (data.template) {
      data.template.content = template.content;
    }
    setShowTemplateDialog(false);
  };

  const handlePopupTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPromptText(event.target.value);
    // Update the template content in the node data
    if (data.template) {
      data.template.content = event.target.value;
    }
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <div
      style={{
        width: 200,
        backgroundColor: 'white',
        border: '1px solid #666666',
        borderRadius: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <DeleteButton nodeId={id} onDelete={onDelete} />
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
          padding: '3px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
          System Prompt
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: 6 }}>
        {/* Template Selection Button */}
        <div style={{ marginBottom: 6 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowTemplateDialog(true)}
            disabled={loading}
            sx={{
              width: '100%',
              fontSize: '8px',
              height: '24px',
              textTransform: 'none',
              borderColor: '#ccc',
              color: '#666',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {loading ? 'Loading...' : 'Select from Templates'}
          </Button>
        </div>

        {/* Text Area */}
        <div style={{ marginBottom: 6 }}>
          <TextField
            multiline
            rows={4}
            value={displayText}
            onChange={handleTextChange}
            placeholder="Enter your system prompt here..."
            variant="outlined"
            size="small"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                fontSize: '9px',
                padding: '2px',
                '& textarea': {
                  padding: '4px',
                  fontSize: '9px',
                  lineHeight: 1.2,
                },
              },
            }}
          />
          {promptText.length > 200 && (
            <Typography variant="caption" color="text.secondary" style={{ fontSize: '8px', marginTop: '4px', display: 'block' }}>
              {promptText.length} characters - Click "Edit Prompt" to see full text
            </Typography>
          )}
        </div>

        {/* Popup Button */}
        <div style={{ marginBottom: 6 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenPopup}
            sx={{
              width: '100%',
              fontSize: '8px',
              height: '24px',
              textTransform: 'none',
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#e3f2fd',
              },
            }}
          >
            Edit Prompt
          </Button>
        </div>

        {/* Output Section */}
        <div>
          <h4 style={{
            margin: '0 0 3px 0',
            textAlign: 'center',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Output
          </h4>

          <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
            <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
              Prompt
            </label>
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              style={{
                background: '#7b1fa2',
                width: 8,
                height: 8,
                border: '1px solid white',
                borderRadius: '50%',
              }}
            />
          </div>
        </div>
      </div>

      {/* Template Selection Dialog */}
      <Dialog
        open={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Prompt Template</Typography>
            <IconButton onClick={() => setShowTemplateDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {templates.map((template) => (
              <Box
                key={template.id}
                sx={{
                  p: 2,
                  mb: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#1976d2',
                  },
                }}
                onClick={() => handleTemplateSelect(template)}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {template.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {template.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Category: {template.category} | Popularity: {template.popularity}%
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup Dialog for Large Text Editing */}
      <Dialog
        open={showPopup}
        onClose={handleClosePopup}
        maxWidth="lg"
        fullWidth
        fullScreen={false}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit System Prompt</Typography>
            <IconButton onClick={handleClosePopup} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <TextField
            multiline
            rows={20}
            value={promptText}
            onChange={handlePopupTextChange}
            placeholder="Enter your system prompt here..."
            variant="outlined"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                '& textarea': {
                  padding: '12px',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  fontFamily: 'monospace',
                },
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePopup} variant="contained">
            Save & Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export const AgentNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const nodes = useNodes();
  const edges = useEdges();
  const { deleteElements } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteElements({ nodes: [{ id }] });
  };

  // Helper function to check if model is OpenAI
  const isOpenAIModel = (provider?: string, model?: string): boolean => {
    // Check provider first
    if (provider && provider.toLowerCase().includes('openai')) {
      return true;
    }

    // Check model name
    if (model && (model.startsWith('gpt-') || model.toLowerCase().includes('openai'))) {
      return true;
    }

    return false;
  };

  // Get connected LLM node data
  const getConnectedLLMData = () => {
    // Find the current agent node ID
    const currentAgentNode = nodes.find(node =>
      node.type === 'agentNode' &&
      (node.data as CustomNodeProps['data'])?.agentData?.id === data.agentData?.id
    );

    if (!currentAgentNode) {
      return null;
    }

    // Find connected LLM node
    const llmNode = nodes.find(node =>
      node.type === 'llmNode' &&
      edges.some(edge =>
        edge.source === node.id &&
        edge.target === currentAgentNode.id &&
        edge.targetHandle === 'llm-input'
      )
    );

    return llmNode ? {
      provider: (llmNode.data as CustomNodeProps['data'])?.provider,
      model: (llmNode.data as CustomNodeProps['data'])?.model
    } : null;
  };

  // Initialize settings from agent data or defaults
  const [agentSettings, setAgentSettings] = React.useState(() => {
    const agentData = data.agentData;
    return {
      show_tool_calls: agentData?.show_tool_calls || false,
      is_shared: agentData?.is_shared || false,
      is_active: agentData?.is_active !== undefined ? agentData.is_active : true,
      markdown: agentData?.agent_metadata?.markdown || false,
      page_context_awareness: agentData?.agent_metadata?.page_context_awareness || false,
      user_context_awareness: agentData?.agent_metadata?.user_context_awareness || false,
      streaming: agentData?.agent_metadata?.streaming || false,
      enable_zeasecure: agentData?.agent_metadata?.enable_zeasecure || false,
      zeasecure_api_key: agentData?.agent_metadata?.zeasecure_api_key || '',
      voice_enabled: agentData?.agent_metadata?.voice_enabled || false,
    };
  });

  // Update settings when agent data changes
  React.useEffect(() => {
    const agentData = data.agentData;
    if (agentData) {
      setAgentSettings({
        show_tool_calls: agentData.show_tool_calls || false,
        is_shared: agentData.is_shared || false,
        is_active: agentData.is_active !== undefined ? agentData.is_active : true,
        markdown: agentData.agent_metadata?.markdown || false,
        page_context_awareness: agentData.agent_metadata?.page_context_awareness || false,
        user_context_awareness: agentData.agent_metadata?.user_context_awareness || false,
        streaming: agentData.agent_metadata?.streaming || false,
        enable_zeasecure: agentData.agent_metadata?.enable_zeasecure || false,
        zeasecure_api_key: agentData.agent_metadata?.zeasecure_api_key || '',
        voice_enabled: agentData.agent_metadata?.voice_enabled || false,
      });
    }
  }, [data.agentData]);

  const handleSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setAgentSettings(prev => ({
      ...prev,
      [name]: checked
    }));

    // Update the node data
    if (data.agentData) {
      if (name === 'markdown' || name === 'page_context_awareness' ||
        name === 'user_context_awareness' || name === 'streaming' ||
        name === 'enable_zeasecure' || name === 'voice_enabled') {
        data.agentData.agent_metadata = {
          ...data.agentData.agent_metadata,
          [name]: checked
        };
      } else {
        data.agentData[name] = checked;
      }
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setAgentSettings(prev => ({
      ...prev,
      [name]: value
    }));

    // Update the node data
    if (data.agentData && name === 'zeasecure_api_key') {
      data.agentData.agent_metadata = {
        ...data.agentData.agent_metadata,
        [name]: value
      };
    }
  };

  const handleAgentNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    data.agentName = value;
    data.label = value; // Also update the label for display
  };

  const handleAgentDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    data.description = value;
  };

  return (
    <>
      <div
        style={{
          width: 160,
          backgroundColor: 'white',
          border: '1px solid #666666',
          borderRadius: 4,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
            padding: '3px 6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <IconButton
              size="small"
              onClick={() => setSettingsOpen(true)}
              style={{
                padding: '2px',
                minWidth: '16px',
                width: '16px',
                height: '16px',
              }}
            >
              <SettingsIcon style={{ fontSize: '12px', color: 'white' }} />
            </IconButton>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>Agent</span>
          </div>
          <IconButton
            size="small"
            onClick={handleDelete}
            style={{
              padding: '2px',
              minWidth: '16px',
              width: '16px',
              height: '16px',
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
            }}
          >
            <DeleteIcon style={{ fontSize: '12px', color: 'white' }} />
          </IconButton>
        </div>

        {/* Content */}
        <div style={{ padding: 6 }}>
          {/* Inputs Section */}
          <div style={{ marginBottom: 6 }}>
            <h4 style={{
              margin: '0 0 3px 0',
              textAlign: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Inputs
            </h4>

            <div style={{ marginBottom: 3 }}>
              <div className="handle-label-pair">
                <Handle
                  type="target"
                  position={Position.Left}
                  id="llm-input"
                  style={{
                    background: '#666666',
                    width: 8,
                    height: 8,
                    border: '1px solid white',
                    borderRadius: '50%',
                  }}
                />
                <label style={{ fontSize: '9px', color: '#666666', fontWeight: 'medium' }}>
                  LLM *
                </label>
              </div>

              <div className="handle-label-pair">
                <Handle
                  type="target"
                  position={Position.Left}
                  id="tools-input"
                  style={{
                    background: '#666',
                    width: 8,
                    height: 8,
                    border: '1px solid white',
                    borderRadius: '50%',
                  }}
                />
                <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
                  Tools
                </label>
              </div>

              <div className="handle-label-pair">
                <Handle
                  type="target"
                  position={Position.Left}
                  id="knowledge-input"
                  style={{
                    background: '#666',
                    width: 8,
                    height: 8,
                    border: '1px solid white',
                    borderRadius: '50%',
                  }}
                />
                <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
                  Knowledge Bases
                </label>
              </div>

              <div className="handle-label-pair">
                <Handle
                  type="target"
                  position={Position.Left}
                  id="prompt-input"
                  style={{
                    background: '#666666',
                    width: 8,
                    height: 8,
                    border: '1px solid white',
                    borderRadius: '50%',
                  }}
                />
                <label style={{ fontSize: '9px', color: '#666666', fontWeight: 'medium' }}>
                  Prompt *
                </label>
              </div>

              <div className="handle-label-pair">
                <Handle
                  type="target"
                  position={Position.Left}
                  id="parser-input"
                  style={{
                    background: '#666666',
                    width: 8,
                    height: 8,
                    border: '1px solid white',
                    borderRadius: '50%',
                  }}
                />
                <label style={{ fontSize: '9px', color: '#666666', fontWeight: 'medium' }}>
                  Output Parser
                </label>
              </div>
            </div>

            {/* Agent Name Field */}
            <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: '8px', color: '#333', display: 'block', marginBottom: 1 }}>
                Agent Name
              </label>
              <input
                type="text"
                placeholder="Name Your Agent"
                defaultValue={data.agentName || ''}
                onChange={handleAgentNameChange}
                style={{
                  width: '100%',
                  padding: '3px 5px',
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5',
                  fontSize: '9px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Agent Description Field */}
            <div>
              <label style={{ fontSize: '8px', color: '#333', display: 'block', marginBottom: 1 }}>
                Description
              </label>
              <textarea
                placeholder="Describe what your agent does..."
                onChange={handleAgentDescriptionChange}
                style={{
                  width: '100%',
                  padding: '3px 5px',
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5',
                  fontSize: '9px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  minHeight: '40px',
                  maxHeight: '80px',
                }}
              />
            </div>
          </div>

          {/* Output Section */}
          <div>
            <h4 style={{
              margin: '0 0 3px 0',
              textAlign: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Output
            </h4>

            <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
              <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
                Agent
              </label>
              <Handle
                type="source"
                position={Position.Right}
                id="output"
                style={{
                  background: '#d32f2f',
                  width: 8,
                  height: 8,
                  border: '1px solid white',
                  borderRadius: '50%',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Agent Settings</Typography>
            <IconButton onClick={() => setSettingsOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Show Tool Calls */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Show Tool Calls</Typography>
                <Typography variant="body2" color="text.secondary">
                  Display tool usage in the conversation
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.show_tool_calls}
                name="show_tool_calls"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Shared Agent */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Shared Agent</Typography>
                <Typography variant="body2" color="text.secondary">
                  To share agent
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.is_shared}
                name="is_shared"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Enable Agent */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Enable Agent</Typography>
                <Typography variant="body2" color="text.secondary">
                  To enable agent
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.is_active}
                name="is_active"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Enable Markdown */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Enable Markdown</Typography>
                <Typography variant="body2" color="text.secondary">
                  Format responses with Markdown
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.markdown}
                name="markdown"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Enable Voice Support */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Enable Voice Support</Typography>
                <Typography variant="body2" color="text.secondary">
                  Allow users to interact with the agent using voice input and receive audio responses
                </Typography>
              </Box>
              <Tooltip
                title={
                  (() => {
                    const llmData = getConnectedLLMData();
                    return !isOpenAIModel(llmData?.provider, llmData?.model)
                      ? "Voice support is only available for OpenAI models (gpt-3.5-turbo, gpt-4, etc.)"
                      : "";
                  })()
                }
                placement="top"
              >
                <span>
                  <Switch
                    checked={agentSettings.voice_enabled}
                    name="voice_enabled"
                    onChange={handleSettingsChange}
                    disabled={(() => {
                      const llmData = getConnectedLLMData();
                      return !isOpenAIModel(llmData?.provider, llmData?.model);
                    })()}
                  />
                </span>
              </Tooltip>
            </Box>

            {/* Enable Page Context Awareness */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Enable Page Context Awareness</Typography>
                <Typography variant="body2" color="text.secondary">
                  Agent will have access to current page HTML
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.page_context_awareness}
                name="page_context_awareness"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Enable User Context Awareness */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Enable User Context Awareness</Typography>
                <Typography variant="body2" color="text.secondary">
                  Agent will have access to current user information
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.user_context_awareness}
                name="user_context_awareness"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Enable Streaming */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">Enable Streaming</Typography>
                <Typography variant="body2" color="text.secondary">
                  Streaming response
                </Typography>
              </Box>
              <Switch
                checked={agentSettings.streaming}
                name="streaming"
                onChange={handleSettingsChange}
              />
            </Box>

            {/* Enable ZeaSecure */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1">Enable ZeaSecure</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enable ZeaSecure
                  </Typography>
                </Box>
                <Switch
                  checked={agentSettings.enable_zeasecure}
                  name="enable_zeasecure"
                  onChange={handleSettingsChange}
                />
              </Box>

              {agentSettings.enable_zeasecure && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="API Key"
                    placeholder="zsk_2e4bxxxxxxxxxxxxxxxxxxxxxxxx2d0c"
                    value={agentSettings.zeasecure_api_key}
                    name="zeasecure_api_key"
                    onChange={handleTextChange}
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 36 }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => setSettingsOpen(false)}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export const ParserNode: React.FC<CustomNodeProps> = React.memo(({ data, id, onDelete }) => {
  const [attributes, setAttributes] = React.useState<Array<{ name: string; description: string; dataType: string }>>(
    data.parserAttributes || []
  );
  const [showAttributesDialog, setShowAttributesDialog] = React.useState(false);
  const [newAttributeName, setNewAttributeName] = React.useState('');
  const [newAttributeDescription, setNewAttributeDescription] = React.useState('');
  const [newAttributeDataType, setNewAttributeDataType] = React.useState('string');

  // Update data when attributes change
  React.useEffect(() => {
    data.parserAttributes = attributes;
  }, [attributes, data]);

  const handleAddAttribute = () => {
    if (newAttributeName.trim() && newAttributeDescription.trim()) {
      const newAttribute = {
        name: newAttributeName.trim(),
        description: newAttributeDescription.trim(),
        dataType: newAttributeDataType
      };
      setAttributes(prev => [...prev, newAttribute]);
      setNewAttributeName('');
      setNewAttributeDescription('');
      setNewAttributeDataType('string');
    }
  };

  const handleRemoveAttribute = (index: number) => {
    setAttributes(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttributeNameChange = (index: number, value: string) => {
    setAttributes(prev => prev.map((attr, i) =>
      i === index ? { ...attr, name: value } : attr
    ));
  };

  const handleAttributeDescriptionChange = (index: number, value: string) => {
    setAttributes(prev => prev.map((attr, i) =>
      i === index ? { ...attr, description: value } : attr
    ));
  };

  const handleAttributeDataTypeChange = (index: number, value: string) => {
    setAttributes(prev => prev.map((attr, i) =>
      i === index ? { ...attr, dataType: value } : attr
    ));
  };

  return (
    <>
      <div
        style={{
          width: 200,
          backgroundColor: 'white',
          border: '1px solid #666666',
          borderRadius: 4,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <DeleteButton nodeId={id} onDelete={onDelete} />
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #666666 0%, #888888 100%)',
            padding: '3px 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <span style={{ fontWeight: 'bold', color: 'white', fontSize: '10px' }}>
            Parser
          </span>
        </div>

        {/* Content */}
        <div style={{ padding: 6 }}>
          {/* Attributes Section */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: '8px', color: '#666', fontWeight: 'bold' }}>
                Attributes ({attributes.length})
              </label>
              <button
                onClick={() => setShowAttributesDialog(true)}
                style={{
                  fontSize: '7px',
                  padding: '2px 4px',
                  border: '1px solid #ccc',
                  borderRadius: '2px',
                  backgroundColor: '#e3f2fd',
                  cursor: 'pointer',
                  color: '#1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}
              >
                <AddIcon sx={{ fontSize: '8px' }} />
                Add
              </button>
            </div>

            {/* Display existing attributes */}
            {attributes.length > 0 && (
              <div style={{
                maxHeight: '80px',
                overflowY: 'auto',
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '4px'
              }}>
                {attributes.map((attr, index) => (
                  <div key={index} style={{
                    fontSize: '7px',
                    marginBottom: '2px',
                    padding: '2px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '2px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>
                      {attr.name} ({attr.dataType})
                    </div>
                    <div style={{ color: '#666', fontSize: '6px' }}>
                      {attr.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Output Section */}
          <div>
            <h4 style={{
              margin: '0 0 3px 0',
              textAlign: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Output
            </h4>

            <div className="handle-label-pair" style={{ justifyContent: 'flex-end' }}>
              <label style={{ fontSize: '9px', color: '#333', fontWeight: 'medium' }}>
                Parser
              </label>
              <Handle
                type="source"
                position={Position.Right}
                id="output"
                style={{
                  background: '#9c27b0',
                  width: 8,
                  height: 8,
                  border: '1px solid white',
                  borderRadius: '50%',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Attributes Configuration Dialog */}
      <Dialog
        open={showAttributesDialog}
        onClose={() => setShowAttributesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Configure Parser Attributes</Typography>
            <IconButton onClick={() => setShowAttributesDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Add New Attribute */}
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add New Attribute
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Attribute Name"
                  placeholder="e.g., title, price, description"
                  value={newAttributeName}
                  onChange={(e) => setNewAttributeName(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Describe what this attribute should extract"
                  value={newAttributeDescription}
                  onChange={(e) => setNewAttributeDescription(e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Data Type</InputLabel>
                  <Select
                    value={newAttributeDataType}
                    onChange={(e) => setNewAttributeDataType(e.target.value)}
                    label="Data Type"
                  >
                    <MenuItem value="string">String</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="boolean">Boolean</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="array">Array</MenuItem>
                    <MenuItem value="object">Object</MenuItem>
                    <MenuItem value="url">URL</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="currency">Currency</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleAddAttribute}
                  disabled={!newAttributeName.trim() || !newAttributeDescription.trim()}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Add Attribute
                </Button>
              </Stack>
            </Box>

            {/* Existing Attributes */}
            {attributes.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Configured Attributes ({attributes.length})
                </Typography>
                <Stack spacing={1}>
                  {attributes.map((attr, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: 'white',
                      }}
                    >
                      <Stack spacing={1}>
                        <TextField
                          fullWidth
                          label="Attribute Name"
                          value={attr.name}
                          onChange={(e) => handleAttributeNameChange(index, e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                        <TextField
                          fullWidth
                          label="Description"
                          value={attr.description}
                          onChange={(e) => handleAttributeDescriptionChange(index, e.target.value)}
                          variant="outlined"
                          size="small"
                          multiline
                          rows={2}
                        />
                        <FormControl fullWidth size="small">
                          <InputLabel>Data Type</InputLabel>
                          <Select
                            value={attr.dataType}
                            onChange={(e) => handleAttributeDataTypeChange(index, e.target.value)}
                            label="Data Type"
                          >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="array">Array</MenuItem>
                            <MenuItem value="object">Object</MenuItem>
                            <MenuItem value="url">URL</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="phone">Phone</MenuItem>
                            <MenuItem value="currency">Currency</MenuItem>
                          </Select>
                        </FormControl>
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveAttribute(index)}
                            startIcon={<DeleteIcon />}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowAttributesDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
